import React, { useState, useEffect } from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const STEPS = [
  {
    num: "01",
    title: "Submit",
    timing: "Takes 5 minutes",
    body: "Upload your release with images and targeting, then choose your package and markets. No calls, no briefs, no back-and-forth.",
    panelLabel: "What you hand us",
    items: ["Your press release and images", "Target markets and sectors", "Your channel mix, earned, wire, or guaranteed"],
  },
  {
    num: "02",
    title: "Review",
    timing: "Same day",
    body: "A real PR strategist, not a form validator, reads your release and sharpens the headline, angle, and structure for the strongest possible pickup.",
    panelLabel: "What our team does",
    items: ["Headline and angle sharpened", "Formatted to newsroom standards", "Flagged back to you if anything blocks pickup"],
  },
  {
    num: "03",
    title: "Distribute",
    timing: "Within 24 hours",
    body: "Your release goes out to your selected channels, newswires, and Google News, with same-day submission.",
    panelLabel: "Where it goes",
    items: ["Your chosen outlets and newswires", "Google News, indexed and searchable", "Journalist inboxes in your target beats"],
  },
  {
    num: "04",
    title: "Report",
    timing: "As coverage lands",
    body: "No screenshots, no vague reach claims. Every placement is a live link you can open, share with investors, and verify yourself.",
    panelLabel: "What you get back",
    items: ["Live published links, one per placement", "Your full outlet list", "Audience and coverage analytics"],
  },
];

export default function HowItWorksPreview() {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused) setStep((s) => (s + 1) % 4);
    }, 4000);
    return () => clearInterval(t);
  }, [paused]);

  const active = STEPS[step];

  return (
    <section id="how" className="py-11 lg:py-12 border-t border-[rgba(28,28,32,0.06)]">
      <Container>
        {/* Heading */}
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] mb-3.5">Process</div>
            <h2 className="font-heading text-[clamp(32px,4vw,46px)] font-[650] tracking-[-0.03em] leading-[1.06] mb-3.5 text-heading-gradient">
              You upload once.<br />We handle the rest.
            </h2>
            <p className="text-[16px] leading-[1.55] text-[#6c6c74] max-w-[420px] mx-auto">
              From submission to distribution in as little as 24 hours.
            </p>
          </div>
        </Reveal>

        {/* Step rail */}
        <div className="flex items-start justify-center mb-9">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-start">
              <button
                onClick={() => { setStep(i); setPaused(true); }}
                className="flex flex-col items-center gap-2.5 w-[120px] bg-transparent border-none cursor-pointer"
                style={{ fontFamily: "inherit" }}
              >
                <span
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full text-[14px] font-bold"
                  style={{
                    background: i === step ? "linear-gradient(135deg, #8b5cf6, #6d28d9)" : i < step ? "rgba(139,92,246,0.12)" : "rgba(255,255,255,0.65)",
                    color: i === step ? "#fff" : i < step ? "#6d28d9" : "#8e8e96",
                    border: `1px solid ${i === step ? "rgba(139,92,246,0.45)" : i < step ? "rgba(139,92,246,0.3)" : "rgba(28,28,32,0.08)"}`,
                    boxShadow: i === step ? "0 8px 20px rgba(124,58,237,0.35)" : "none",
                  }}
                >
                  {s.num}
                </span>
                <span
                  className="text-[13px] whitespace-nowrap"
                  style={{ fontWeight: i === step ? 650 : 500, color: i === step ? "#1c1c20" : "#8e8e96" }}
                >
                  {s.title}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="h-[2px] w-[60px] mt-[22px] rounded-full"
                  style={{ background: i < step ? "linear-gradient(90deg, #8b5cf6, #6d28d9)" : "rgba(28,28,32,0.1)" }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Active step panel */}
        <div className="glass-card max-w-[760px] mx-auto p-7 lg:p-9" style={{ borderRadius: 28 }}>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6d28d9]">{active.timing}</span>
          </div>
          <h3 className="text-[22px] font-[650] tracking-[-0.02em] mb-2.5">{active.title}</h3>
          <p className="text-[14.5px] leading-[1.6] text-[#55555c] mb-5">{active.body}</p>

          {/* Inner accent panel */}
          <div className="rounded-[18px] p-5" style={{ background: "linear-gradient(160deg, rgba(139,92,246,0.09), rgba(255,255,255,0.4))", border: "1px solid rgba(255,255,255,0.85)" }}>
            <div className="text-[11px] font-semibold text-[#8e8e96] mb-3.5">{active.panelLabel}</div>
            <div className="flex flex-col gap-2.5">
              {active.items.map((item) => (
                <div key={item} className="flex items-start gap-2.5">
                  <span className="check-circle mt-[1px]">✓</span>
                  <span className="text-[13px] text-[#55555c] leading-[1.45]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-9">
          <Link to="/how-it-works"
            className="inline-block text-[14px] font-semibold px-6 py-3 rounded-xl bg-[rgba(139,92,246,0.09)] border border-[rgba(139,92,246,0.2)] text-[#6d28d9] whitespace-nowrap transition-colors hover:bg-[rgba(139,92,246,0.16)]">
            See how it works <ArrowRight className="inline w-3.5 h-3.5 ml-1" />
          </Link>
        </div>
      </Container>
    </section>
  );
}